import React from 'react';
import { Link } from 'react-router-dom';
import useSolutions from '../../hooks/useSolutions';

const AllSolutionsGrid = () => {
  const { solutions, loading, error } = useSolutions();

  // Helper function to render rich text description
  const renderDescription = (description) => {
    if (!description) return '';
    
    if (typeof description === 'string') {
      return description;
    }
    
    if (Array.isArray(description)) {
      return description.map(block => {
        if (block.type === 'paragraph' && block.children) {
          return block.children.map(child => child.text || '').join('');
        }
        return '';
      }).join(' ');
    }
    
    return '';
  };

  // Loading state
  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-catalina-blue mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading solutions...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md mx-auto">
              <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-bold text-red-600 mb-2">Error Loading Solutions</h3>
              <p className="text-red-500">{error}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (!solutions || solutions.length === 0) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-2xl font-bold text-gray-600 mb-2">No Solutions Available</h3>
            <p className="text-gray-500">Check back soon for our solutions and services.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-catalina-blue mb-4">All Solutions We Provide</h2>
          <p className="text-lg text-gray-600">
            Comprehensive services to support your material handling needs
          </p>
          <div className="mt-4 text-sm text-gray-500">
            Showing {solutions.length} {solutions.length === 1 ? 'solution' : 'solutions'}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {solutions.map((solution) => {
            const title = solution.Title || solution.title;
            const description = renderDescription(solution.Description || solution.description);
            const slug = solution.slug;

            return (
              <div
                key={solution.id}
                className="bg-white rounded-3xl shadow-lg p-8 hover:shadow-xl transition duration-300 border border-gray-100"
              >
                <div className="bg-catalina-blue rounded-2xl w-16 h-16 flex items-center justify-center mb-6">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-catalina-blue mb-4">{title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed line-clamp-4">{description}</p>
                <Link
                  to={`/solution/${slug}`}
                  className="inline-flex items-center text-catalina-blue font-semibold hover:text-tango transition duration-300"
                >
                  Learn More
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-catalina-blue mb-4">Need a Custom Solution?</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Our team of experts is ready to help you find the perfect solution for your specific needs. 
            Contact us today for a free consultation.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-catalina-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-san-marino transition duration-300"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AllSolutionsGrid;